import { Component, OnInit, DestroyRef, inject, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { EmailsService } from '../../../core/services/emails.service';
import { EmailCategory, EmailListItem, EmailThread, ThreadMessage } from '../../../core/models/email.model';

type LoadMode = 'draft' | 'intake' | 'blacklisted';

@Component({
  selector: 'app-email-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './email-detail.component.html',
  styleUrls: ['./email-detail.component.css']
})
export class EmailDetailComponent implements OnInit {

  private destroyRef = inject(DestroyRef);

  // Thread mode (whitelisted/greylisted)
  thread: EmailThread | null = null;
  originalMessage: ThreadMessage | null = null;
  replyMessages: ThreadMessage[] = [];
  missingFieldsList: string[] = [];
  private systemMessageIds = new Set<number>();

  // Simple mode (blacklisted — no draft, router state is the fast path)
  blacklistedEmail: EmailListItem | null = null;

  isLoading = false;
  /** Translate key, empty when there is no error */
  errorMessage = '';
  emailType: EmailCategory = 'white-listed';
  replyText = '';
  replyFiles: File[] = [];
  isSendingReply = false;
  /** Translate keys, empty when nothing to show */
  replyError = '';
  replySuccess = '';

  @ViewChild('replyFileInput') replyFileInput!: ElementRef;

  private id = 0;
  private loadMode: LoadMode = 'draft';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private emailsService: EmailsService
  ) {}

  ngOnInit() {
    // Blacklisted email passed via router state (fast path, lost on refresh)
    const navState = history.state;
    if (navState?.email) {
      this.blacklistedEmail = navState.email as EmailListItem;
    }

    combineLatest([this.route.params, this.route.queryParams])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, queryParams]) => {
        this.id = +params['id'];
        this.emailType = (queryParams['type'] as EmailCategory) || 'white-listed';

        if (!this.id || isNaN(this.id)) {
          this.errorMessage = 'ED_NOT_FOUND';
          return;
        }

        this.loadMode = this.emailType === 'black-listed' ? 'blacklisted'
          : queryParams['by'] === 'intake' ? 'intake'
          : 'draft';
        this.load();
      });
  }

  get isBlacklisted(): boolean {
    return this.emailType === 'black-listed';
  }

  /** Translate key for the status badge; raw value as fallback for unknown statuses */
  get statusKey(): string {
    const status = this.thread?.parsingStatus as string | undefined;
    switch (status) {
      case 'COMPLETE':     return 'ED_STATUS_COMPLETE';
      case 'PENDING_INFO': return 'PENDING_INFO';
      // Intake threads carry the raw intake status in parsingStatus
      case 'NON_TRUSTED':  return 'UNTRUSTED_DOMAIN';
      default:             return status || '';
    }
  }

  get blacklistStatusKey(): string {
    switch (this.blacklistedEmail?.status) {
      case 'SPAM':        return 'EC_REASON_SPAM';
      case 'IRRELEVANT':  return 'EC_REASON_IRRELEVANT';
      case 'PROMOTIONAL': return 'EC_REASON_PROMOTIONAL';
      default:            return this.blacklistedEmail?.status || '';
    }
  }

  // ── Loading ─────────────────────────────────────────────────────────────

  retry() {
    this.errorMessage = '';
    this.load();
  }

  private load() {
    if (this.loadMode === 'blacklisted') {
      if (!this.blacklistedEmail) this.loadBlacklistedEmail(this.id);
    } else if (this.loadMode === 'intake') {
      this.loadIntakeThread(this.id);
    } else {
      this.loadThread(this.id);
    }
  }

  private loadThread(draftId: number) {
    this.isLoading = true;
    this.errorMessage = '';

    this.emailsService.getEmailThread(draftId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (thread) => {
          this.setThread(thread);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'ED_LOAD_ERROR';
        }
      });
  }

  /** Greylisted rows without a draft (e.g. untrusted domain) have no draft thread */
  private loadIntakeThread(intakeId: number) {
    this.isLoading = true;
    this.errorMessage = '';

    this.emailsService.getIntakeThread(intakeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (thread) => {
          this.setThread(thread);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'ED_LOAD_ERROR';
        }
      });
  }

  /** Refresh-safe fallback for blacklisted emails: rebuild the row from the intake thread. */
  private loadBlacklistedEmail(intakeId: number) {
    this.isLoading = true;
    this.errorMessage = '';

    this.emailsService.getIntakeThread(intakeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (thread) => {
          const original = thread.messages?.find(m => m.type === 'ORIGINAL') || thread.messages?.[0];
          if (original) {
            this.blacklistedEmail = {
              intakeId: original.intakeId,
              senderEmail: original.senderEmail,
              senderDisplayName: original.senderDisplayName,
              subject: original.subject,
              bodySnippet: original.bodyText,
              receivedAt: original.receivedAt,
              // for intake threads the backend carries the intake status (SPAM etc.) here
              status: thread.parsingStatus as unknown as string,
            };
          } else {
            this.errorMessage = 'ED_DATA_UNAVAILABLE';
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'ED_DATA_UNAVAILABLE';
        }
      });
  }

  /** Derive everything the template needs once per load instead of per change-detection cycle. */
  private setThread(thread: EmailThread) {
    this.thread = thread;
    this.originalMessage = thread.messages?.find(m => m.type === 'ORIGINAL') || null;
    this.replyMessages = thread.messages?.filter(m => m.type === 'REPLY') || [];
    this.missingFieldsList = thread.missingFields
      ? thread.missingFields.split(',').map(f => f.trim()).filter(f => f)
      : [];
    this.systemMessageIds = new Set(
      this.replyMessages.filter(m => this.detectSystemMessage(m)).map(m => m.intakeId)
    );
  }

  // ── Navigation ──────────────────────────────────────────────────────────

  goBack() {
    this.location.back();
  }

  // ── Thread Helpers ──────────────────────────────────────────────────────

  isSystemMessage(message: ThreadMessage): boolean {
    return this.systemMessageIds.has(message.intakeId);
  }

  // Heuristic until the backend tags messages with a sender type.
  // System replies use "GRMIS Support", admin replies "GRMIS Support (Admin)".
  private detectSystemMessage(message: ThreadMessage): boolean {
    return message.senderEmail?.includes('grmis') ||
           message.senderEmail?.includes('system') ||
           !!message.senderDisplayName?.startsWith('GRMIS Support');
  }

  getSenderInitials(name: string, fallback?: string): string {
    const source = name || fallback || '';
    if (!source) return 'U';
    return source
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getConfidenceLabel(): string {
    if (!this.thread) return '';
    const c = this.thread.aiConfidence;
    if (c >= 0.8) return 'High';
    if (c >= 0.5) return 'Medium';
    return 'Low';
  }

  getConfidenceClass(): string {
    if (!this.thread) return '';
    const c = this.thread.aiConfidence;
    if (c >= 0.8) return 'confidence-high';
    if (c >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  }

  // ── Reply ────────────────────────────────────────────────────────────────

  triggerReplyFileInput() {
    this.replyFileInput?.nativeElement.click();
  }

  onReplyFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.replyFiles = [...this.replyFiles, ...Array.from(input.files)];
    input.value = '';
  }

  removeReplyFile(index: number) {
    this.replyFiles = this.replyFiles.filter((_, i) => i !== index);
  }

  sendReply() {
    const body = this.replyText.trim();
    const intakeId = this.originalMessage?.intakeId;
    if (!body || !intakeId || this.isSendingReply) return;

    this.isSendingReply = true;
    this.replyError = '';
    this.replySuccess = '';

    this.emailsService.sendAdminReply(intakeId, body, this.replyFiles)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSendingReply = false;
          if (response.sent) {
            this.replyText = '';
            this.replyFiles = [];
            this.replySuccess = 'ED_REPLY_SENT';
            this.load();
          } else {
            this.replyError = 'ED_REPLY_ERROR';
          }
        },
        error: () => {
          this.isSendingReply = false;
          this.replyError = 'ED_REPLY_ERROR';
        }
      });
  }

  trackByMessage(index: number, message: ThreadMessage): number {
    return message.intakeId;
  }
}
